import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { CreateBoatInput, UpdateBoatInput } from '@/lib/marina/validators'

export interface Boat {
  id: string
  name: string
  type: 'POWERBOAT' | 'SAILING_BOAT' | 'YACHT' | 'CATAMARAN' | 'FISHING' | 'DINGHY' | 'OTHER'
  loaMeters: string
  beamMeters: string
  vesselType: 'PRIVATE' | 'COMMERCIAL'
  color: string | null
  manufacturer: string | null
  model: string | null
  yearBuilt: number | null
  draftMeters: string | null
  weightTons: string | null
  fuelCapacityL: string | null
  waterCapacityL: string | null
  registration: string | null
  imoNumber: string | null
  hullId: string | null
  flagState: string | null
  portOfRegistry: string | null
  photo: string | null
  location: string
  notes: string | null
  customerId: string
  customer?: {
    id: string
    firstName: string
    lastName: string
    category: string
  }
  createdAt: string
  updatedAt: string
}

export interface BoatsListResponse {
  items: Boat[]
  total: number
  page: number
  pageSize: number
}

export interface BoatsListParams {
  q?: string
  customerId?: string
  type?: Boat['type']
  flagState?: string
  page?: number
  pageSize?: number
  sort?: string
  order?: 'asc' | 'desc'
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, { credentials: 'include', ...init })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Request failed' }))
    throw new Error(err.error || `HTTP ${res.status}`)
  }
  return res.json()
}

function toQuery(params: Record<string, unknown>): string {
  const qs = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') qs.set(k, String(v))
  }
  const s = qs.toString()
  return s ? `?${s}` : ''
}

export function useBoats(params: BoatsListParams = {}) {
  return useQuery({
    queryKey: ['boats', params],
    queryFn: () => apiFetch<BoatsListResponse>(`/api/boats${toQuery(params as Record<string, unknown>)}`),
    staleTime: 15_000,
  })
}

export function useBoat(id: string | null | undefined) {
  return useQuery({
    queryKey: ['boat', id],
    queryFn: () => apiFetch<{ boat: Boat }>(`/api/boats/${id}`),
    enabled: !!id,
  })
}

export function useCreateBoat() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateBoatInput) =>
      apiFetch<{ boat: Boat }>('/api/boats', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['boats'] })
      qc.invalidateQueries({ queryKey: ['customers'] })
    },
  })
}

export function useUpdateBoat() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateBoatInput }) =>
      apiFetch<{ boat: Boat }>(`/api/boats/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['boats'] })
      qc.invalidateQueries({ queryKey: ['boat', vars.id] })
    },
  })
}

export function useDeleteBoat() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiFetch<{ ok: boolean }>(`/api/boats/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['boats'] }),
  })
}
